import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel } from "@/components/common";

export default function ScoringConfig() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [d, setD] = useState(null);
  const [label, setLabel] = useState("");
  const [gates, setGates] = useState({ newness: 60, breakout: 60, commerce: 40 });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/scoring-config").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (d) {
      const active = d.versions.find((v) => v.config_version === d.active_version);
      if (active?.verdict?.emerging) setGates({ ...active.verdict.emerging });
    }
  }, [d]);

  if (!d) return <div className="p-8 text-[#71717A] font-mono text-sm">Loading…</div>;
  const active = d.versions.find((v) => v.config_version === d.active_version);

  const createActivate = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/scoring-config", {
        label: label || `gates-${gates.newness}/${gates.breakout}/${gates.commerce}`,
        edits: { verdict: { emerging: { ...active.verdict.emerging, newness: Number(gates.newness), breakout: Number(gates.breakout), commerce: Number(gates.commerce) } } },
      });
      await api.post(`/scoring-config/${data.config_version}/activate`);
      const rc = await api.post("/recompute-all");
      setMsg(`Activated config v${data.config_version} and recomputed ${rc.data.recomputed} products. Historical scores retain their original version.`);
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-8 max-w-[1100px]">
      <h1 className="font-head text-2xl font-bold tracking-tight mb-1">Scoring Config</h1>
      <p className="text-xs font-mono text-[#71717A] mb-6">
        Every weight & threshold is versioned. Activating a new version recalculates current scores only —
        historical score rows keep the version that produced them.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <Panel title={`Active config — v${d.active_version} (${active?.label || ""})`}>
            <pre data-testid="active-config-json" className="text-[11px] font-mono text-[#A1A1AA] overflow-x-auto max-h-[420px]">
              {JSON.stringify(
                { velocity: active?.velocity, newness: active?.newness, breakout: active?.breakout, trendquality: active?.trendquality, commerce: active?.commerce, confidence: active?.confidence, verdict: active?.verdict },
                null,
                2
              )}
            </pre>
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel title="Edit EMERGING gates → new version">
            {!isAdmin && <div className="text-xs font-mono text-amber-400 mb-3">Read-only (analyst role). Admin required to edit.</div>}
            <div className="grid grid-cols-3 gap-3">
              {["newness", "breakout", "commerce"].map((k) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono">{k} ≥</label>
                  <input
                    data-testid={`gate-${k}`}
                    type="number"
                    disabled={!isAdmin}
                    value={gates[k]}
                    onChange={(e) => setGates({ ...gates, [k]: e.target.value })}
                    className="mt-1 w-full bg-[#09090B] border border-[#27272A] focus:border-[#71717A] outline-none px-3 py-2 text-sm font-mono disabled:opacity-50"
                  />
                </div>
              ))}
            </div>
            <input
              data-testid="config-label"
              disabled={!isAdmin}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="version label (optional)"
              className="mt-3 w-full bg-[#09090B] border border-[#27272A] focus:border-[#71717A] outline-none px-3 py-2 text-sm font-mono disabled:opacity-50"
            />
            <button
              data-testid="create-config-button"
              disabled={!isAdmin || busy}
              onClick={createActivate}
              className="mt-3 w-full bg-emerald-500 hover:bg-emerald-400 text-[#09090B] font-semibold text-sm py-2.5 disabled:opacity-50"
            >
              {busy ? "Applying…" : "Create version, activate & recompute"}
            </button>
            {msg && <div data-testid="config-message" className="mt-3 text-[11px] font-mono text-emerald-400">{msg}</div>}
          </Panel>

          <Panel title={`Version history [${d.versions.length}]`}>
            <div className="space-y-1">
              {d.versions.map((v) => (
                <div key={v.config_version} className="flex items-center justify-between border border-[#1c1c1f] px-3 py-2 font-mono text-xs">
                  <span>
                    v{v.config_version} · {v.label}
                  </span>
                  {v.config_version === d.active_version ? (
                    <span className="text-emerald-400">ACTIVE</span>
                  ) : (
                    <span className="text-[#71717A]">gates {v.verdict?.emerging?.newness}/{v.verdict?.emerging?.breakout}/{v.verdict?.emerging?.commerce}</span>
                  )}
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
